import { Alert, Badge, Card, DataTable, EmptyState, LoadingState, PageHeader, Spinner, Stat, StatGrid } from '@platform/ui';
import { Link } from '@tanstack/react-router';

import { ListError } from '../components/ListError';
import { useMessages } from '../i18n';
import type { LapsedAccount } from '../lib/api';
import { formatDate, formatPhone, useFormat } from '../lib/format';
import { usePageTitle } from '../lib/page-title';
import { channelDetailText, useFailingChannels, useLapsedAccounts, useOverLimitAccounts, useSummary } from '../lib/platform';

/** From the seventh day after an unpaid period ended, staff call (ADR 0017 §4). */
const CALL_AFTER_DAYS = 7;

/** Whole days since an instant, a day begun not yet counted. */
function daysSince(value: string, now: Date = new Date()): number {
    return Math.max(0, Math.floor((now.getTime() - new Date(value).getTime()) / 86_400_000));
}

/**
 * What platform staff see first.
 *
 * Failing connections come above the figures, not below them. A connection that
 * has stopped working is the quietest failure this platform has — nothing
 * breaks visibly, orders simply stop arriving — and seeing it before the seller
 * does is most of what support is for. Beneath them, the accounts above a
 * limit of their package and those whose subscription has expired: still
 * shipping, unable to grow, and worth a call before they go.
 */
export function OverviewPage() {
    const t = useMessages();
    usePageTitle(t.overview.title);
    const format = useFormat();
    const summary = useSummary();
    const failing = useFailingChannels();
    const overLimit = useOverLimitAccounts();
    const lapsed = useLapsedAccounts();

    if (summary.isPending) {
        return <LoadingState label={t.loading} />;
    }
    if (summary.isError || !summary.data) {
        return <Alert tone="danger">{t.genericError}</Alert>;
    }

    const data = summary.data;
    const channels = failing.data?.channels ?? [];
    const accountLink = (tenantId: string, name: string) => (
        <Link to="/hesaplar/$tenantId" params={{ tenantId }} className="text-primary-strong hover:underline">
            {name}
        </Link>
    );

    return (
        <div className="flex flex-col gap-6">
            <PageHeader title={t.overview.title} description={t.overview.subtitle} />

            <Card title={t.overview.failing}>
                <div className="flex flex-col gap-3">
                    <p className="text-muted-foreground">{t.overview.failingHint}</p>
                    <DataTable
                        rows={channels}
                        rowKey={(row) => row.accountId}
                        caption={t.overview.failing}
                        empty={<EmptyState title={t.overview.failingEmpty} />}
                        columns={[
                            {
                                key: 'tenant',
                                header: t.accounts.columns.account,
                                cell: (row) => row.tenantName,
                            },
                            {
                                key: 'channel',
                                header: t.accounts.columns.channels,
                                narrow: true,
                                cell: (row) => row.accountName,
                            },
                            {
                                key: 'detail',
                                header: t.accounts.columns.detail,
                                // Already reduced to something safe to show: the connection
                                // module never lets a credential reach here.
                                cell: (row) => <span className="text-muted-foreground">{row.detail ? channelDetailText(t, row.detail) : '—'}</span>,
                            },
                        ]}
                    />
                </div>
            </Card>

            <StatGrid>
                <Stat label={t.overview.accounts} value={data.tenants} />
                <Stat label={t.overview.activeAccounts} value={data.activeTenants} />
                <Stat label={t.overview.users} value={data.users} />
                <Stat label={t.overview.products} value={data.products} />
                <Stat label={t.overview.orders} value={data.orders} />
                <Stat label={t.overview.failingChannels} value={data.failingChannels} tone={data.failingChannels > 0 ? 'danger' : 'default'} />
            </StatGrid>

            <Card title={t.overview.lapsed} description={t.overview.lapsedHint}>
                {lapsed.isError && <ListError query={lapsed} />}
                {lapsed.isPending && <Spinner label={t.loading} />}
                {lapsed.data && (
                    <DataTable
                        rows={lapsed.data.accounts}
                        rowKey={(row) => row.tenantId}
                        caption={t.overview.lapsed}
                        empty={<EmptyState title={t.overview.lapsedEmpty} />}
                        columns={[
                            { key: 'account', header: t.accounts.columns.account, cell: (row) => accountLink(row.tenantId, row.tenantName) },
                            { key: 'package', header: t.overview.columns.package, narrow: true, cell: (row) => row.planName },
                            {
                                key: 'contact',
                                header: t.overview.columns.contact,
                                cell: (row) => <LapsedContact account={row} />,
                            },
                            {
                                key: 'periodEnd',
                                header: t.overview.columns.periodEnd,
                                narrow: true,
                                cell: (row) => <span className="tabular-nums">{formatDate(row.periodEnd)}</span>,
                            },
                            {
                                key: 'since',
                                header: t.overview.columns.since,
                                narrow: true,
                                cell: (row) => {
                                    if (!row.periodEnd) return '—';
                                    const days = daysSince(row.periodEnd);
                                    return (
                                        <span className="flex flex-col items-start gap-1">
                                            <span className="tabular-nums">{t.overview.daysSince(days)}</span>
                                            {days >= CALL_AFTER_DAYS && <Badge tone="warning">{t.overview.callNow}</Badge>}
                                        </span>
                                    );
                                },
                            },
                        ]}
                    />
                )}
            </Card>

            <Card title={t.overview.overLimit} description={t.overview.overLimitHint}>
                {overLimit.isError && <ListError query={overLimit} />}
                {overLimit.isPending && <Spinner label={t.loading} />}
                {overLimit.data && (
                    <DataTable
                        rows={overLimit.data.accounts}
                        rowKey={(row) => `${row.tenantId}:${row.resource}`}
                        caption={t.overview.overLimit}
                        empty={<EmptyState title={t.overview.overLimitEmpty} />}
                        columns={[
                            { key: 'account', header: t.accounts.columns.account, cell: (row) => accountLink(row.tenantId, row.tenantName) },
                            { key: 'package', header: t.overview.columns.package, narrow: true, cell: (row) => row.planName },
                            { key: 'limit', header: t.overview.columns.limit, narrow: true, cell: (row) => t.limits[row.resource] },
                            {
                                key: 'usage',
                                header: t.overview.columns.usage,
                                align: 'end',
                                narrow: true,
                                cell: (row) => (
                                    <span className="text-destructive-strong tabular-nums">
                                        {format.count(row.used)} / {format.count(row.limit)}
                                    </span>
                                ),
                            },
                        ]}
                    />
                )}
            </Card>
        </div>
    );
}

/** Who to call: the owner by name, their e-mail and the company's phone. */
function LapsedContact({ account }: { account: LapsedAccount }) {
    return (
        <span className="flex flex-col gap-1">
            <span>{account.ownerName || account.ownerEmail || '—'}</span>
            {account.ownerEmail && <span className="text-muted-foreground">{account.ownerEmail}</span>}
            {account.phone && <span className="text-muted-foreground tabular-nums">{formatPhone(account.phone)}</span>}
        </span>
    );
}
